/**
 * /fiyatlar sayfasının tek veri kaynağı (istemci + SSR + JSON-LD).
 *
 * KURAL: Burada yalnızca sitede zaten taahhüt edilen gerçek koşullar yer alır.
 * Reklam danışmanlığı / yönetimi paketlerinin aylık tutarı henüz belirlenmediği için
 * `fiyat: null` bırakıldı ve sayfada "Teklif ile" görünür. Tutar netleştiğinde yalnızca
 * ilgili paketin `fiyat` alanına sayı (TL, KDV hariç, aylık) yazmanız yeterlidir;
 * sayfa, SSR ve Offer şeması otomatik güncellenir.
 */

export interface KiralamaPaketi {
  id: string;
  platform: string;
  baslik: string;
  aciklama: string;
  komisyon: string;
  komisyonNot: string;
  ozellikler: string[];
  detay: string;
  whatsapp: string;
  oneCikan?: boolean;
}

export interface DanismanlikPaketi {
  id: string;
  baslik: string;
  aciklama: string;
  /** Aylık tutar (TL, KDV hariç). null → "Teklif ile". 0 → "Ücretsiz". */
  fiyat: number | null;
  donem: string;
  fiyatNot: string;
  ozellikler: string[];
  detay?: string;
  whatsapp: string;
  oneCikan?: boolean;
}

export const WHATSAPP_NO = "905320522545";
export const waLink = (mesaj: string) => `https://wa.me/${WHATSAPP_NO}?text=${encodeURIComponent(mesaj)}`;

export const KIRALAMA_PAKETLERI: KiralamaPaketi[] = [
  {
    id: "meta",
    platform: "Meta",
    baslik: "Facebook & Instagram Ajans Hesabı",
    aciklama: "Limite takılan veya kurumsal faturalandırma isteyen markalar için yüksek limitli Meta ajans reklam hesabı.",
    komisyon: "%3'ten başlar",
    komisyonNot: "reklam bakiyesi üzerinden, hacim büyüdükçe düşer",
    ozellikler: [
      "Aynı gün aktivasyon (yaklaşık 2 saat)",
      "Kurulum ücreti yok, minimum bütçe yok",
      "Kendi Business Manager'ınıza partner olarak bağlanır",
      "Pixel, katalog ve kitleler sizde kalır",
      "Reklam reddi ve itiraz desteği dahil",
      "Kurumsal fatura, gizli kesinti yok",
    ],
    detay: "/facebook-reklam-hesabi-kiralama",
    whatsapp: "Merhaba, Facebook & Instagram ajans reklam hesabı kiralama için teklif almak istiyorum.",
    oneCikan: true,
  },
  {
    id: "google",
    platform: "Google",
    baslik: "Google Ads Ajans Hesabı",
    aciklama: "Limite takılan veya kurumsal faturalandırma isteyen markalar için kurumsal doğrulamalı Google Ads ajans hesabı.",
    komisyon: "%3'ten başlar",
    komisyonNot: "reklam bakiyesi üzerinden, hacim büyüdükçe düşer",
    ozellikler: [
      "Arama, Alışveriş, Performance Max ve YouTube",
      "Kurulum ücreti yok",
      "Harcamalar panelde anlık görünür",
      "Kurumsal fatura, gizli kesinti yok",
    ],
    detay: "/hizmetler/google-ads-ajans-hesabi",
    whatsapp: "Merhaba, Google Ads ajans hesabı için teklif almak istiyorum.",
  },
  {
    id: "tiktok",
    platform: "TikTok",
    baslik: "TikTok Ajans Hesabı",
    aciklama: "TikTok Ads için yüksek limitli ajans hesabı ve bakiye yönetimi.",
    komisyon: "%3'ten başlar",
    komisyonNot: "reklam bakiyesi üzerinden, hacim büyüdükçe düşer",
    ozellikler: [
      "Aynı gün kurulum",
      "Kurulum ücreti yok",
      "Harcamalar panelde anlık görünür",
      "Kurumsal fatura, gizli kesinti yok",
    ],
    detay: "/hizmetler/tiktok-ajans-hesabi",
    whatsapp: "Merhaba, TikTok ajans hesabı için teklif almak istiyorum.",
  },
];

export const DANISMANLIK_PAKETLERI: DanismanlikPaketi[] = [
  {
    id: "denetim",
    baslik: "Hesap Denetimi & Danışmanlık Görüşmesi",
    aciklama: "Hesabınız kapandıysa, limitler yetmiyorsa veya kampanyalar beklenen sonucu vermiyorsa ilk adım.",
    fiyat: 0,
    donem: "",
    fiyatNot: "ilk görüşme ve ön denetim",
    ozellikler: [
      "15 dakikalık görüşmede durum analizi",
      "Hesap, kampanya yapısı ve ölçümleme ön denetimi",
      "Hesap kapanma sebebi teşhisi",
      "Somut iyileştirme listesi",
    ],
    detay: "/hizmetler/reklam-hesabi-kapandi-cozum",
    whatsapp: "Merhaba, reklam hesabım için ücretsiz denetim ve danışmanlık görüşmesi istiyorum.",
  },
  {
    id: "yonetim",
    baslik: "Reklam Yönetimi & Danışmanlık",
    aciklama: "Meta, Google ve TikTok kampanyalarınızı uzman ekip uçtan uca yönetir.",
    fiyat: null,
    donem: "/ay",
    fiyatNot: "aylık yönetim ücreti; kanal sayısı ve harcamaya göre",
    ozellikler: [
      "Kampanya kurulumu ve haftalık optimizasyon",
      "Kreatif yönlendirme ve politika kontrolü",
      "ROAS odaklı bütçe dağılımı",
      "Haftalık raporlama",
      "Ajans hesabıyla birlikte alınırsa avantajlı komisyon",
    ],
    detay: "/hizmetler/reklam-yonetimi",
    whatsapp: "Merhaba, reklam yönetimi ve danışmanlık için teklif almak istiyorum.",
    oneCikan: true,
  },
  {
    id: "e-ticaret",
    baslik: "E-Ticaret Reklam Yönetimi",
    aciklama: "Online mağazalar için katalog, ölçümleme ve yeniden pazarlama odaklı yönetim.",
    fiyat: null,
    donem: "/ay",
    fiyatNot: "aylık yönetim ücreti; ürün sayısı ve kanallara göre",
    ozellikler: [
      "Katalog ve Advantage+ alışveriş kampanyaları",
      "Pixel ve Conversions API kurulumu",
      "Dinamik yeniden pazarlama",
      "ROAS odaklı bütçe yönetimi",
      "ikas, Shopify, Ticimax, İdeasoft, T-Soft, WooCommerce",
    ],
    detay: "/hizmetler/e-ticaret-reklam-yonetimi",
    whatsapp: "Merhaba, e-ticaret reklam yönetimi için teklif almak istiyorum.",
  },
];

/** Danışmanlık paketinin görünen fiyatı */
export function danismanlikFiyati(p: DanismanlikPaketi): string {
  if (p.fiyat === 0) return "Ücretsiz";
  if (p.fiyat == null) return "Teklif ile";
  return `₺${p.fiyat.toLocaleString("tr-TR")}`;
}

/** Tüm hizmetlerde geçerli koşullar */
export const FIYAT_KOSULLARI: { baslik: string; deger: string }[] = [
  { baslik: "Kurulum ücreti", deger: "Yok" },
  { baslik: "Minimum reklam bütçesi", deger: "Yok" },
  { baslik: "Hesap aktivasyonu", deger: "Aynı gün (yaklaşık 2 saat)" },
  { baslik: "Limit artışı", deger: "Ek ücret yok" },
  { baslik: "Faturalandırma", deger: "Kurumsal fatura" },
  { baslik: "Gizli kesinti", deger: "Yok; harcama panelde anlık görünür" },
];

/** Örnek komisyon hesabı (başlangıç oranıyla) */
export const ORNEK_HESAP = { harcama: 300000, oran: 3 };

export const FIYAT_SSS: { q: string; a: string }[] = [
  {
    q: "Reklam hesabı kiralama ücreti nasıl hesaplanır?",
    a: "Yüklediğiniz reklam bakiyesi üzerinden önceden anlaşılan bir hizmet komisyonu alınır. Komisyon %3'ten başlar ve aylık harcama hacminiz büyüdükçe düşer. Örneğin aylık 300.000 TL harcamada %3 komisyon 9.000 TL'dir.",
  },
  {
    q: "Kurulum ücreti veya minimum bütçe şartı var mı?",
    a: "Hayır. Kurulum ücreti ve minimum bütçe şartı yoktur. Limit artışı için de ek ücret alınmaz.",
  },
  {
    q: "Hesap ne kadar sürede açılır?",
    a: "Uygunluk kontrolünden sonra ajans hesabı genellikle aynı gün, yaklaşık 2 saat içinde tahsis edilir ve Business Manager'ınıza bağlanır.",
  },
  {
    q: "Reklam danışmanlığı ve yönetimi nasıl fiyatlandırılıyor?",
    a: "Reklam yönetimi aylık yönetim ücretiyle çalışır. Tutar, yönetilecek kanal sayısına, aylık harcamaya ve kreatif ihtiyacına göre belirlenir. İlk görüşme ve ön denetim ücretsizdir; net teklifi görüşmenin sonunda iletiriz.",
  },
  {
    q: "Reklam yönetimi için önerilen bütçe nedir?",
    a: "Sağlıklı optimizasyon için aylık toplam 30.000 TL ve üzeri reklam bütçesi öneriyoruz. Daha düşük bütçelerde algoritmalar öğrenme fazından çıkamadığı için sonuçlar dalgalı olur.",
  },
  {
    q: "Hesap kiralama ve reklam yönetimini birlikte alabilir miyim?",
    a: "Evet. Ajans hesabı ve reklam yönetimini birlikte alan markalara avantajlı komisyon uygulanır.",
  },
  {
    q: "Ödeme ve faturalandırma nasıl?",
    a: "Reklam bütçesi hesaba bakiye olarak yüklenir ve harcamalar anlık izlenir. Hizmet bedelleri için kurumsal fatura düzenlenir.",
  },
];
